import React from "react";
import { useDraggable } from "@dnd-kit/core";
import { Heading } from "../ui/Heading";
import { FaFont, FaShapes, FaSquare } from "react-icons/fa";

interface ComponentPaletteProps {
  onAddElement: (type: "text" | "shape" | "frame", content?: string) => void;
  availableShapes?: Array<{ id: string; name: string; url: string; category?: string }>;
  textOnly?: boolean; // If true, only show text component (for 3D designer)
}

interface DraggableComponentItemProps {
  id: string;
  type: "text" | "shape" | "frame";
  label: string;
  icon: React.ReactNode;
  onAddElement: (type: "text" | "shape" | "frame", content?: string) => void;
}

interface DraggableComponentItemProps {
  id: string;
  type: "text" | "shape" | "frame";
  label: string;
  icon: React.ReactNode;
  onAddElement: (type: "text" | "shape" | "frame", content?: string) => void;
  firstShapeUrl?: string;
}

const DraggableComponentItem: React.FC<DraggableComponentItemProps> = ({
  id,
  type,
  label,
  icon,
  onAddElement,
  firstShapeUrl,
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    data: {
      type,
      content: type === "shape" ? firstShapeUrl : (type === "text" ? "New Text" : ""),
    },
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  const handleClick = () => {
    onAddElement(type);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`
        flex items-center gap-3 p-3 border border-gray-300 rounded-lg
        cursor-grab active:cursor-grabbing
        hover:bg-gray-50 hover:border-blue-400
        transition-colors
        ${isDragging ? "opacity-50" : ""}
      `}
      onClick={handleClick}
    >
      <div className="text-gray-600">{icon}</div>
      <span className="text-sm font-medium text-gray-700">{label}</span>
    </div>
  );
};

const ComponentPalette: React.FC<ComponentPaletteProps> = ({ onAddElement, availableShapes = [], textOnly = false }) => {
  const allComponents = [
    {
      id: "component-text",
      type: "text" as const,
      label: "Text",
      icon: <FaFont className="w-5 h-5" />,
    },
    {
      id: "component-shape",
      type: "shape" as const,
      label: "Shape",
      icon: <FaShapes className="w-5 h-5" />,
    },
    {
      id: "component-frame",
      type: "frame" as const,
      label: "Frame",
      icon: <FaSquare className="w-5 h-5" />,
    },
  ];
  
  const components = textOnly 
    ? allComponents.filter(c => c.type === "text")
    : allComponents.filter(c => {
        // Hide shape component if no shapes are available
        if (c.type === "shape" && availableShapes.length === 0) {
          return false;
        }
        return true;
      });

  // Get first available shape URL for drag data
  const getFirstShapeUrl = () => {
    return availableShapes.length > 0 ? availableShapes[0].url : "";
  };

  return (
    <div className="p-4">
      <Heading size="sm" className="mb-4">
        Components
      </Heading>
      <div className="space-y-2">
        {components.map((component) => (
          <DraggableComponentItem
            key={component.id}
            id={component.id}
            type={component.type}
            label={component.label}
            icon={component.icon}
            onAddElement={onAddElement}
            firstShapeUrl={component.type === "shape" ? getFirstShapeUrl() : ""}
          />
        ))}
      </div>
    </div>
  );
};

export default ComponentPalette;
